// A trace's walkthrough, as the workspace's chat runs it and shows the story
// it tells (components/Walkthrough.jsx): one investigator walks the reader
// through a session. From an outline of its record
// (lib/sessionOutline.js) it picks the moments that tell its story, reads
// them, checks acts against what ran on the machine, and pins a note on each
// on the trace's card (lib/traceNoteStore.js): the ask, the decisions and
// acts that shaped it, what went wrong, and how it ended, whether or not the
// review flagged anything (its findings are leads, not the story). The chat
// lists the notes in order under the story they tell.
//
// One walkthrough a session, kept in this browser for the signed-in reader;
// walked again, the new one replaces it, its notes on the card too. A run is
// the browser's while the page is open: it carries on with the chat closed.
//
// A workspace holding several sessions has one of its own (`ws:<id>`, see
// workspaceWalkKey): one story across them, its moments on their cards
// numbered across them all, and how they connect (`links`). Its notes are
// kept by a key of their own (walkthroughChatKey), so a board shows its own
// walkthrough's moments and never two walkthroughs' numbers on one card.
//
// The store is light, for the board's bar and the chat's card; what a run
// reads and asks of the model (lib/traceWalkthroughRun.js) is loaded when a
// walkthrough first starts.
import { useSyncExternalStore } from "react";
import { onAuthIdentityChange } from "./authTokenStore.js";
import { browserStorageKey } from "./browserStorageScope.js";
import { addTraceNotes, cleanCovers, removeTraceNotes } from "./traceNoteStore.js";

const PREFIX = "origin-trace-walkthroughs-v1";
// Sessions whose walkthrough is kept, the most recently walked.
export const KEPT_WALKTHROUGHS = 60;
// Who the walkthrough's notes on a card say left them.
export const WALKTHROUGH_BY = { key: "walkthrough", title: "Walkthrough" };
export const walkNotePrefix = (session) => `walk:${lower(session)}:`;
// A workspace's walkthrough, across the sessions on it, and the key its
// notes are kept by (a trace's own are WALKTHROUGH_BY's).
export const workspaceWalkKey = (workspaceId) => `ws:${lower(workspaceId)}`;
export const isWorkspaceWalk = (key) => lower(key).startsWith("ws:");
export const walkthroughChatKey = (key) => (isWorkspaceWalk(key) ? `${WALKTHROUGH_BY.key}:${lower(key)}` : WALKTHROUGH_BY.key);

const str = (v) => (v == null ? "" : String(v));
const lower = (v) => str(v).trim().toLowerCase();
const clip = (v, n) => {
    const s = str(v).trim();
    return s.length > n ? `${s.slice(0, n - 1)}...` : s;
};
const IDLE = Object.freeze({ status: "idle", question: "", now: "", account: "", notes: [], links: [], sessions: [], read: null, window: null, at: 0, costUsd: 0, claims: 0, cases: 0, error: "" });

// -- the store ---------------------------------------------------------------

const live = new Map();
const runs = new Map();
const subscribers = new Set();
let kept = null;
let keptOwner = undefined;
let version = 0;
const emit = () => {
    version += 1;
    for (const fn of subscribers) fn();
};
onAuthIdentityChange(() => {
    kept = null;
    keptOwner = undefined;
    live.clear();
    emit();
});

// What this reader keeps, read once per reader: { [session]: walkthrough }.
function keptAll() {
    const owner = browserStorageKey(PREFIX);
    if (kept && keptOwner === owner) return kept;
    keptOwner = owner;
    kept = {};
    if (!owner) return kept;
    try {
        const raw = JSON.parse(globalThis.localStorage?.getItem(owner) || "{}");
        for (const [s, w] of Object.entries(raw || {})) {
            const clean = cleanKept(w);
            if (clean) kept[lower(s)] = clean;
        }
    } catch { /* unreadable: nothing kept */ }
    return kept;
}

// A walkthrough as kept: a workspace's notes each say their session, its
// links join two of its sessions' messages; a moment keeps the claims and
// case notes it covers.
const count = (v) => Math.max(0, Math.floor(Number(v) || 0));
const keptCovers = (n) => {
    const covers = cleanCovers(n?.covers);
    return covers.length ? { covers } : {};
};
// When a moment's message was said, as the record the walkthrough read gave
// it, and whether that is only when Origin first received it.
const keptHappened = (n) => (Number.isFinite(n?.happenedAt) ? { happenedAt: n.happenedAt, ...(n.happenedApprox ? { happenedApprox: true } : {}) } : {});
const cleanEnd = (e) => (e?.session && e.messageId ? { session: lower(e.session), messageId: str(e.messageId) } : null);
const cleanWindow = (w) => (Number.isFinite(w?.fromMs) && Number.isFinite(w?.toMs) ? { fromMs: w.fromMs, toMs: w.toMs } : null);
function cleanKept(w) {
    if (!w || typeof w !== "object") return null;
    const notes = (Array.isArray(w.notes) ? w.notes : [])
        .filter((n) => n?.id && n.messageId && str(n.text).trim())
        .map((n) => ({ id: str(n.id), ...(n.session ? { session: lower(n.session) } : {}), messageId: str(n.messageId), text: str(n.text).trim(), kind: n.kind === "matters" ? "matters" : "context", ...keptCovers(n), ...keptHappened(n) }));
    const links = (Array.isArray(w.links) ? w.links : [])
        .map((l) => ({ id: str(l?.id), from: cleanEnd(l?.from), to: cleanEnd(l?.to), text: str(l?.text).trim() }))
        .filter((l) => l.id && l.from && l.to && l.text);
    const sessions = (Array.isArray(w.sessions) ? w.sessions : []).filter((x) => x?.id).map((x) => ({ id: lower(x.id), title: str(x.title) }));
    if (!notes.length && !str(w.account).trim()) return null;
    // The sessions a workspace's read, of those it was handed (it reads a few at most).
    const read = Array.isArray(w.read) ? w.read.map(lower).filter(Boolean) : null;
    return { status: "done", question: str(w.question), now: "", account: str(w.account), notes, links, sessions, read, window: cleanWindow(w.window), at: Number(w.at) || 0, costUsd: Number(w.costUsd) || 0, claims: count(w.claims), cases: count(w.cases), error: "" };
}

function keep(session, w) {
    const all = { ...keptAll(), [session]: w };
    const names = Object.keys(all).sort((a, b) => (all[b].at || 0) - (all[a].at || 0)).slice(0, KEPT_WALKTHROUGHS);
    kept = Object.fromEntries(names.map((n) => [n, all[n]]));
    const owner = keptOwner;
    if (!owner) return;
    const kept1 = (n) => [n, { question: kept[n].question, account: kept[n].account, notes: kept[n].notes, links: kept[n].links, sessions: kept[n].sessions, read: kept[n].read, window: kept[n].window, at: kept[n].at, costUsd: kept[n].costUsd, claims: kept[n].claims, cases: kept[n].cases }];
    // The browser's storage full, the oldest go first, so the one just walked
    // stays kept with the pins it left on the cards; refused even alone, it
    // is kept for this page.
    for (let count = names.length; count > 0; count = Math.floor(count / 2)) {
        try {
            globalThis.localStorage?.setItem(owner, JSON.stringify(Object.fromEntries(names.slice(0, count).map(kept1))));
            return;
        } catch { /* fewer, then */ }
    }
}

// A note's headline (its first sentence, as the walkthrough writes them) and
// the rest of what it says.
export function splitNote(text) {
    const m = /^(.{3,120}?[.!?])\s+([\s\S]+)$/.exec(String(text || "").trim());
    return m ? [m[1], m[2]] : [String(text || "").trim(), ""];
}

/** A session's walkthrough: running, done, or idle when none was walked. */
export function walkthroughOf(session) {
    const s = lower(session);
    if (!s) return IDLE;
    return live.get(s) || keptAll()[s] || IDLE;
}

/** A number that changes whenever any walkthrough does. */
export function useWalkthroughsVersion() {
    return useSyncExternalStore((fn) => { subscribers.add(fn); return () => subscribers.delete(fn); }, () => version, () => version);
}

export function useWalkthrough(session) {
    const s = lower(session);
    useSyncExternalStore((fn) => { subscribers.add(fn); return () => subscribers.delete(fn); }, () => version, () => version);
    return walkthroughOf(s);
}

/**
 * Which walkthrough a trace's workspace shows: of the trace's own (`own`)
 * and the workspace's (`ws`), the one under way, else the one walked last,
 * else `next`, the one a walkthrough would start now. Traces laid on or
 * taken off leave the walkthrough the reader has, and its moments, in place
 * until they walk through again.
 */
export function shownWalkKey({ own = null, ws = null, next = null } = {}) {
    const a = own ? walkthroughOf(own) : IDLE;
    const b = ws ? walkthroughOf(ws) : IDLE;
    if (b.status === "running") return ws;
    if (a.status === "running") return own;
    // One that just failed or was stopped says so (only a run started on
    // this page leaves one).
    if (b.status === "error" || b.status === "stopped") return ws;
    if (a.status === "error" || a.status === "stopped") return own;
    const held = (w) => !!(w.account || w.notes.length);
    if (held(a) && held(b)) return (b.at || 0) >= (a.at || 0) ? ws : own;
    if (held(b)) return ws;
    if (held(a)) return own;
    return next;
}

function set(session, w) {
    live.set(session, w);
    emit();
}
function patch(session, fn) {
    const cur = live.get(session);
    if (cur) set(session, { ...cur, ...fn(cur) });
}

/**
 * Walks the reader through a session: `title` names it, `question` is what
 * the reader asked it to look out for. Resolves once it is done, stopped or
 * failed; a session already being walked through is left to finish. A
 * workspace's (`session` its workspaceWalkKey) walks through `sessions` ([{
 * id, title }]) together. `claims`: what the workspace's chat claimed
 * (lib/walkClaims.js), leads it reads, with `readChat()` reading that chat
 * for where one came from; `cases`: what an investigation made of the case
 * the workspace was opened for (lib/walkCases.js), leads too. Each moment
 * keeps the claims and case notes it covers.
 */
export async function startWalkthrough(session, { title = "", question = "", sessions = null, claims = [], cases = [], readChat = null } = {}) {
    const s = lower(session);
    if (!s || runs.has(s)) return walkthroughOf(s);
    const many = isWorkspaceWalk(s);
    const laid = many ? (sessions || []).filter((x) => x?.id).map((x) => ({ id: lower(x.id), title: str(x.title) })) : [];
    if (many && !laid.length) return walkthroughOf(s);
    const controller = new AbortController();
    runs.set(s, controller);
    const asked = clip(question, 400);
    // The last walkthrough stays in view, its notes on the card, until this
    // one replaces it, and if this one fails or is stopped.
    const before = keptAll()[s] || IDLE;
    set(s, { ...before, status: "running", question: asked, now: "Reading the trace's record", error: "" });
    try {
        const runner = await import("./traceWalkthroughRun.js");
        const onStep = (line) => patch(s, () => ({ now: line }));
        const run = many
            ? await runner.walkThroughWorkspace({ sessions: laid, question: asked, claims, cases, readChat, signal: controller.signal, onStep })
            : await runner.walkThrough({ session: s, title, question: asked, claims, cases, readChat, signal: controller.signal, onStep });
        if (!run.ok) {
            set(s, { ...before, status: run.aborted ? "stopped" : "error", question: asked, error: run.aborted ? "" : clip(run.error, 300) || "the model call did not complete" });
            return walkthroughOf(s);
        }
        const at = Date.now();
        const notes = run.notes.map((n, i) => ({ id: `${walkNotePrefix(s)}${at}:${i}`, ...(many ? { session: lower(n.session) } : {}), messageId: n.messageId, text: n.text, kind: n.kind === "matters" ? "matters" : "context", ...keptCovers(n), ...keptHappened(n) }));
        const links = many ? (run.links || []).map((l, i) => ({ id: `${walkNotePrefix(s)}${at}:L${i}`, from: cleanEnd(l.from), to: cleanEnd(l.to), text: str(l.text) })).filter((l) => l.from && l.to && l.text) : [];
        // The walkthrough it replaces comes off the cards with its notes.
        removeTraceNotes(many ? [...new Set([...laid.map((x) => x.id), ...before.sessions.map((x) => x.id)])] : [s], walkNotePrefix(s));
        const chat = many ? { key: walkthroughChatKey(s), title: WALKTHROUGH_BY.title } : WALKTHROUGH_BY;
        addTraceNotes(notes.map((n, i) => ({
            id: n.id, session: many ? n.session : s, messageId: n.messageId, text: n.text,
            ...(n.kind === "matters" ? { kind: "matters" } : {}), chat, ...(many ? { n: i + 1 } : {}), ...(n.covers ? { covers: n.covers } : {}), at,
        })));
        const done = { ...IDLE, status: "done", question: asked, account: run.account, notes, links, sessions: laid, read: many && Array.isArray(run.read) ? run.read.map(lower) : null, window: cleanWindow(run.window), at, costUsd: run.costUsd || 0, claims: claims.length, cases: cases.length };
        live.delete(s);
        keep(s, done);
        emit();
        return done;
    } catch (error) {
        set(s, { ...before, status: "error", question: asked, error: clip(error?.message || error, 300) });
        return walkthroughOf(s);
    } finally {
        runs.delete(s);
    }
}

/** Stops a session's walkthrough while it runs. */
export function stopWalkthrough(session) {
    runs.get(lower(session))?.abort();
}

// The walkthrough beside a board, for that board's own bar and the chat
// worked on it to start, by the walkthrough's key (components/
// Walkthrough.jsx BoardWalkthrough): { key, walks, status, held, start, stop,
// board, sessions, turnLabel, claimN }, or null with no board in front
// holding one (one beside it in a split has none, and never another
// board's). `start(question)` walks through `walks` and settles with the
// walkthrough as it ends, `stop` stops one under way, `board` is the
// workspace's id, for the chat worked on it to find its own (boardWalkOf);
// the rest is what the story the chat shows says of the board.
const boardWalks = new Map();
const boardWalkSubs = new Set();
export function publishBoardWalk(key, next) {
    const k = lower(key);
    if (!k) return;
    const cur = boardWalks.get(k) || null;
    const same = (a, b) => (!a && !b) || (!!a && !!b && ["key", "walks", "status", "held", "start", "stop", "board", "sessions", "turnLabel", "claimN"].every((f) => a[f] === b[f]));
    if (same(cur, next || null)) return;
    if (next) boardWalks.set(k, next);
    else boardWalks.delete(k);
    for (const fn of boardWalkSubs) fn();
}
export function useBoardWalk(key) {
    const k = lower(key);
    const read = () => (k ? boardWalks.get(k) || null : null);
    return useSyncExternalStore((fn) => { boardWalkSubs.add(fn); return () => boardWalkSubs.delete(fn); }, read, read);
}
/** The walkthrough beside the board of the workspace `boardId`, as useBoardWalk has it, or null. */
export function boardWalkOf(boardId) {
    const b = str(boardId);
    return b ? [...boardWalks.values()].find((w) => w.board === b) || null : null;
}

// Test and harness seam: a walkthrough held as if it had run.
export function seedWalkthrough(session, w) {
    const s = lower(session);
    const clean = cleanKept(w);
    if (clean) keep(s, clean);
    emit();
}
export function _resetWalkthroughsForTests() {
    boardWalks.clear();
    for (const c of runs.values()) c.abort();
    runs.clear();
    live.clear();
    kept = null;
    keptOwner = undefined;
    emit();
}
